import { z } from "zod";
import { GROUP_DISPLAY_NAME_MAX_LENGTH, GROUP_NAME_MAX_LENGTH, GROUP_PURPOSE_MAX_LENGTH } from "./groups.js";
import { ADMIN_GROUP_ROLE_REASON_MAX_LENGTH } from "./admin-group-roles.js";

/**
 * Shared DTO — SUPER_ADMIN hỗ trợ nhóm (US-AE4.F6-0856 nối tiếp · US-AE4.F6-0857):
 *  - `@UC9873` danh sách link mời + thu hồi từng link
 *  - `@UC9874` sửa tên hiển thị thành viên (CHỈ tên suất — QĐ-3)
 *  - `@UC9875` sửa tên / mô tả nhóm
 *
 * Độ dài dùng LẠI hằng của app (`GROUP_*_MAX_LENGTH`) — không chép số: lệch trần là chỗ này cho qua chỗ
 * kia chặn. Request `.strict()`; actor lấy từ phiên admin, không bao giờ từ body.
 */

const reasonSchema = z.string().trim().min(1).max(ADMIN_GROUP_ROLE_REASON_MAX_LENGTH);

/**
 * Trạng thái link mời HIỂN THỊ cho nhân viên — dẫn xuất lúc đọc từ `Invitation.status` + hạn dùng +
 * số lượt (không để UI tự suy từ ba cột rồi suy sai):
 * `active` còn dùng được · `expired` quá hạn · `used_up` đã hết lượt · `revoked` đã thu hồi · `closed` khác.
 */
export const adminInviteLinkStatusSchema = z.enum(["active", "expired", "used_up", "revoked", "closed"]);
export type AdminInviteLinkStatus = z.infer<typeof adminInviteLinkStatusSchema>;

/** Một dòng link mời. 🔴 KHÔNG có `token` / `tokenHash` (hash cũng là đường vào) — `.strict()` chặn rò. */
export const adminInviteLinkItemSchema = z
  .object({
    invitationId: z.string(),
    status: adminInviteLinkStatusSchema,
    createdAt: z.string(),
    expiresAt: z.string().nullable(),
    /** Tên người tạo (null = không rõ / đã ẩn danh). */
    createdByName: z.string().nullable(),
    usedCount: z.number().int().nonnegative(),
    /** null = không giới hạn lượt. */
    maxUses: z.number().int().positive().nullable(),
  })
  .strict();
export type AdminInviteLinkItem = z.infer<typeof adminInviteLinkItemSchema>;

export const adminInviteLinkListResponseSchema = z.object({
  data: z.array(adminInviteLinkItemSchema),
  pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }),
});
export type AdminInviteLinkListResponse = z.infer<typeof adminInviteLinkListResponseSchema>;

/** Body POST /v1/admin/groups/:id/invite-links/:invId/revoke. */
export const adminRevokeInviteLinkBodySchema = z
  .object({ reason: reasonSchema, supportTicketId: z.string().uuid().optional() })
  .strict();
export type AdminRevokeInviteLinkBody = z.infer<typeof adminRevokeInviteLinkBodySchema>;

export const adminRevokeInviteLinkResponseSchema = z.object({
  invitationId: z.string(),
  status: z.literal("revoked"),
});
export type AdminRevokeInviteLinkResponse = z.infer<typeof adminRevokeInviteLinkResponseSchema>;

/** Body PATCH /v1/admin/groups/:id/members/:memberId/display-name — chỉ tên SUẤT (QĐ-3). */
export const adminEditMemberDisplayNameBodySchema = z
  .object({
    displayName: z.string().trim().min(1).max(GROUP_DISPLAY_NAME_MAX_LENGTH),
    reason: reasonSchema,
    supportTicketId: z.string().uuid().optional(),
  })
  .strict();
export type AdminEditMemberDisplayNameBody = z.infer<typeof adminEditMemberDisplayNameBodySchema>;

export const adminEditMemberDisplayNameResponseSchema = z.object({
  groupMemberId: z.string(),
  displayName: z.string(),
});
export type AdminEditMemberDisplayNameResponse = z.infer<typeof adminEditMemberDisplayNameResponseSchema>;

/** Body PATCH /v1/admin/groups/:id/profile — tên / mô tả; KHÔNG ảnh · tiền tệ · loại nhóm. */
export const adminEditGroupProfileBodySchema = z
  .object({
    name: z.string().trim().min(1).max(GROUP_NAME_MAX_LENGTH).optional(),
    /** `null` = xoá mô tả, khác `undefined` = không đổi (luật app). */
    purpose: z.string().trim().max(GROUP_PURPOSE_MAX_LENGTH).nullable().optional(),
    reason: reasonSchema,
    supportTicketId: z.string().uuid().optional(),
  })
  .strict()
  .refine((b) => b.name !== undefined || b.purpose !== undefined, { message: "Cần ít nhất một thông tin để cập nhật" });
export type AdminEditGroupProfileBody = z.infer<typeof adminEditGroupProfileBodySchema>;

export const adminEditGroupProfileResponseSchema = z.object({
  groupId: z.string(),
  name: z.string(),
  purpose: z.string().nullable(),
});
export type AdminEditGroupProfileResponse = z.infer<typeof adminEditGroupProfileResponseSchema>;

/** Mã lỗi ổn định (problem+json `code`). */
export const ADMIN_GROUP_SUPPORT_CODE = {
  /** Nội dung mới vẫn chứa từ cấm (cùng bộ lọc với job quét nội dung hằng ngày). */
  CONTENT_BLOCKED: "ADMIN_GROUP_SUPPORT_CONTENT_BLOCKED",
  /** Suất đã rời / bị gỡ / đã ẩn danh — không ghi lại tên đã được xoá. */
  MEMBER_NOT_EDITABLE: "ADMIN_GROUP_SUPPORT_MEMBER_NOT_EDITABLE",
} as const;
